// mutations.ts
// React Query mutations for Tours module

import { useMutation, useQueryClient, type UseMutationOptions } from "@tanstack/react-query";

import { createTour, updateTour, deleteTour, aiImportTour } from "./api";
import type { AiImportOptions } from "./api";
import { ToursKeys } from "./queries";

import type {
  TourHeader,
  CreateTourWithDetailsPayload,
  UpdateTourWithDetailsPayload,
  AiTourImportResult,
} from "./types";

/* =========================================================
 * AI IMPORT – useAiImportTour
 * ======================================================= */

type AiImportVariables = { files: File[]; options?: AiImportOptions };

/** Extract structured tour data from uploaded itinerary documents. */
export function useAiImportTour(
  options?: UseMutationOptions<AiTourImportResult, Error, AiImportVariables>
) {
  return useMutation<AiTourImportResult, Error, AiImportVariables>({
    mutationFn: ({ files, options: opts }) => aiImportTour(files, opts),
    ...options,
  });
}

/* =========================================================
 * CREATE – useCreateTour
 * ======================================================= */

type CreateTourVariables = CreateTourWithDetailsPayload;
type CreateTourResult = TourHeader;
type CreateTourError = Error;

export function useCreateTour(
  options?: UseMutationOptions<CreateTourResult, CreateTourError, CreateTourVariables>
) {
  const queryClient = useQueryClient();

  return useMutation<CreateTourResult, CreateTourError, CreateTourVariables>({
    mutationFn: (payload) => createTour(payload),
    onSuccess: (data, variables, context, mutation) => {
      // Invalidate all tour lists so the new tour appears
      queryClient.invalidateQueries({ queryKey: ToursKeys.lists() });

      // Call caller's onSuccess if provided
      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    ...options,
  });
}

/* =========================================================
 * UPDATE – useUpdateTour
 * ======================================================= */

type UpdateTourVariables = {
  id: number;
  payload: UpdateTourWithDetailsPayload;
};
type UpdateTourResult = TourHeader;
type UpdateTourError = Error;

export function useUpdateTour(
  options?: UseMutationOptions<UpdateTourResult, UpdateTourError, UpdateTourVariables>
) {
  const queryClient = useQueryClient();

  return useMutation<UpdateTourResult, UpdateTourError, UpdateTourVariables>({
    mutationFn: ({ id, payload }) => updateTour(id, payload),
    onSuccess: (data, variables, context, mutation) => {
      const { id } = variables;

      // Invalidate lists + this tour's detail view
      queryClient.invalidateQueries({ queryKey: ToursKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ToursKeys.detail(id) });

      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    ...options,
  });
}

/* =========================================================
 * DELETE – useDeleteTour
 * ======================================================= */

type DeleteTourVariables = number; // id
type DeleteTourResult = string; // success message
type DeleteTourError = Error;

export function useDeleteTour(
  options?: UseMutationOptions<DeleteTourResult, DeleteTourError, DeleteTourVariables>
) {
  const queryClient = useQueryClient();

  return useMutation<DeleteTourResult, DeleteTourError, DeleteTourVariables>({
    mutationFn: (id) => deleteTour(id),
    onSuccess: (data, variables, context, mutation) => {
      const id = variables;

      // Invalidate lists and this specific detail if it's in cache
      queryClient.invalidateQueries({ queryKey: ToursKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ToursKeys.detail(id) });

      if (options?.onSuccess) {
        options.onSuccess(data, variables, context, mutation);
      }
    },
    ...options,
  });
}
